import type { Building } from "building-types/building/buildingTypes";
import type { FacadeSpanStratum, FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RWTable, Table } from "common/data/table";
import { ENTITY_ID_SEPARATOR } from "doc/entity/componentInstanceEntityResolve";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { removeFormulaDisabledStrata } from "../utils/facade";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({ doc, optionChoices, entityDisabledByGroup, enabledFeatures, facades }: Building) {
  const definitions = docToOptionDefinitions(doc);
  const optionChoicesSpanStrata = resolveOptionsInFormula(doc.facadeSpanStrata, definitions, optionChoices);
  const optionChoicesSweepStrata = resolveOptionsInFormula(doc.facadeSweepStrata, definitions, optionChoices);

  return {
    optionChoicesSpanStrata,
    optionChoicesSweepStrata,
    entityDisabledByGroup,
    facadeSweepStrata: doc.facadeSweepStrata,
    facadeSpanStrata: doc.facadeSpanStrata,
    facades,
    definitions,
    optionChoices,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  optionChoicesSpanStrata: "shallow",
  optionChoicesSweepStrata: "shallow",
  entityDisabledByGroup: "shallow",
  enabledFeatures: "shallow",
});

function generate({
  optionChoicesSpanStrata,
  optionChoicesSweepStrata,
  entityDisabledByGroup,
  facadeSweepStrata,
  facadeSpanStrata,
  facades,
  definitions,
  optionChoices,
  enabledFeatures,
}: ReturnType<typeof select>): {
  facadeSweepStrata: Building["facadeSweepStrata"];
  facadeSpanStrata: Building["facadeSpanStrata"];
} {
  const evaluatedSpanStratum = evalFormulas(
    facadeSpanStrata,
    (v) => optionChoicesSpanStrata[v],
    undefined,
    entityDisabledByGroup,
  );

  const evaluatedSweepStratum = evalFormulas(
    facadeSweepStrata,
    (v) => optionChoicesSweepStrata[v],
    undefined,
    entityDisabledByGroup,
  );

  const finalSpanStrata: RWTable<FacadeSpanStratum> = { ...evaluatedSpanStratum };
  const finalSweepStrata: RWTable<FacadeSweepStratum> = { ...evaluatedSweepStratum };

  if (enabledFeatures.modelComponents) {
    for (const facadeId in facades) {
      const facade = facades[facadeId];
      if (!facade || !facade.sourceId || !facade.componentInstanceChain) {
        continue;
      }

      const { sourceId, componentInstanceChain } = facade;

      if (componentInstanceChain.length === 0) {
        continue;
      }

      addInstancedStrata(
        facadeSpanStrata,
        finalSpanStrata,
        sourceId,
        facadeId,
        definitions,
        optionChoices,
        entityDisabledByGroup,
        componentInstanceChain,
      );

      addInstancedStrata(
        facadeSweepStrata,
        finalSweepStrata,
        sourceId,
        facadeId,
        definitions,
        optionChoices,
        entityDisabledByGroup,
        componentInstanceChain,
      );
    }
  }

  return {
    facadeSweepStrata: removeFormulaDisabledStrata(finalSweepStrata),
    facadeSpanStrata: removeFormulaDisabledStrata(finalSpanStrata),
  };
}

function addInstancedStrata<T extends FacadeSpanStratum | FacadeSweepStratum>(
  sourceStrata: Table<T>,
  targetStrata: RWTable<T>,
  sourceId: string,
  instancedFacadeId: string,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  entityDisabledByGroup: Table<boolean>,
  componentInstanceChain: readonly string[],
) {
  const instanceId = componentInstanceChain[0];
  for (const stratumId in sourceStrata) {
    const stratum = sourceStrata[stratumId];
    if (stratum.parentId === sourceId) {
      const instanceStratumId = `${stratumId}${ENTITY_ID_SEPARATOR}${instanceId}`;
      const componentStratum = evaluateEntityForComponentInstance(
        stratum,
        componentInstanceChain,
        definitions,
        optionChoices,
      );

      if (!componentStratum.disabled && !entityDisabledByGroup[componentStratum.id]) {
        targetStrata[instanceStratumId] = {
          ...componentStratum,
          id: instanceStratumId,
          parentId: instancedFacadeId,
        };
      }
    }
  }
}

export default memo(select, generate, compare);
